/**
 * @fileoverview PHARMAKON — the « Journal des avoirs fournisseur » store (refonte WP-30)
 * @module stores/avoirStore
 *
 * Holds one journal page (rows, total, the état counts and the footer the server computes over the
 * whole filter) and its query. It reads the server envelope through `lireJournalAvoirs` (the
 * AUD-AVOIR-02 contract: the former store destructured the rows array and the journal was always
 * empty). A late answer never overwrites a newer one (request sequence + AbortSignal).
 *
 * The writes (règlement, rapprochement, annulation) return the server's avoir to their caller and
 * re-read the page silently (rows stay on screen); a refused write is thrown to the dialog that sent
 * it — it never replaces the journal with an error screen (AUD-AVOIR-26).
 */
import { create } from "zustand";
import { avoirApi } from "../services/index.js";
import { createRequestSequence, isAbortError } from "../lib/request-sequence.js";
import { lireJournalAvoirs } from "../lib/avoir-journal.js";
import { AVOIR_JOURNAL } from "@pharmakon/shared/constants/avoirs.js";

const sequenceListe = createRequestSequence();
const donnees = (r) => r?.data ?? r;

export const useAvoirStore = create((set, get) => ({
  avoirs: [],
  total: 0,
  page: 1,
  limit: AVOIR_JOURNAL.limit,
  compteurs: null,
  totaux: null,
  loading: false,
  error: null,
  /** The query (filters, `etat`, sort) built by the page (`lib/avoir-journal.js` `paramsJournalAvoirs`). */
  params: null,

  /** A new query: back to page 1 (or the page it carries). */
  setParams: (params) => set({ params, page: Number(params?.page ?? 1) }),
  setPage: (page) => set((s) => ({ page, params: s.params ? { ...s.params, page } : s.params })),

  /**
   * Read the current page. `silencieux` (after a write): the rows stay while the page is re-read and
   * a failed re-read leaves them as they were.
   */
  fetch: async ({ silencieux = false } = {}) => {
    const { params, page, limit } = get();
    if (!params) return;
    const ticket = sequenceListe.begin();
    if (!silencieux) set({ loading: true, error: null });
    try {
      const r = await avoirApi.list({ ...params, page, limit }, { signal: ticket.signal });
      if (!sequenceListe.isCurrent(ticket)) return;
      const j = lireJournalAvoirs(r);
      set({ avoirs: j.avoirs, total: j.total, compteurs: j.compteurs, totaux: j.totaux, loading: false, error: null });
    } catch (e) {
      if (isAbortError(e) || !sequenceListe.isCurrent(ticket)) return;
      if (silencieux) return;
      set({ error: e ?? { message: null }, loading: false });
    }
  },

  /** « Enregistrer le règlement » / « Rapprocher… » (a règlement with `note_de_credit_id`). */
  regler: async (id, payload) => {
    const r = await avoirApi.regler(id, payload);
    void get().fetch({ silencieux: true });
    return donnees(r);
  },

  /** Refonte WP-83 — « Confirmer l'avoir » (D-32): the avoir CONFIRMÉ, with its `reliquat` after a partial confirmation. */
  confirmer: async (id, body) => {
    const r = await avoirApi.confirmer(id, body);
    void get().fetch({ silencieux: true });
    return donnees(r);
  },

  /** « Annuler l'avoir » (OP-03-10): number kept, motif required. */
  annuler: async (id, motif) => {
    const r = await avoirApi.annuler(id, motif);
    void get().fetch({ silencieux: true });
    return donnees(r);
  },
}));

export default useAvoirStore;
